import {
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { Add16Regular, Checkmark12Filled, Delete16Regular, ReOrderDotsVertical16Regular } from "@fluentui/react-icons";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { MockCaseMenu, type MockCase } from "@/features/access/MockCaseMenu";
import { usePlanSurface } from "@/features/access/use-plan-access";
import { AppDialog, ConfirmDialog } from "@/components/app-dialog";
import { PageHeaderTitle } from "@/components/ui-typography";
import { FuiButton } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { LabelColor, TaskBucket, TaskLabel } from "@/engine/types";
import { LABEL_COLORS } from "@/store/execution-task-ops";
import { useProjectStore } from "@/store/project-store";
import { LABEL_DOT_COLOR, LABEL_STYLE } from "./task-ui";

const ROW_H = "32px";

const useStyles = makeStyles({
  headingRow: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    minWidth: 0,
  },
  headingTitle: {
    flex: "0 1 auto",
    width: "fit-content",
    maxWidth: "100%",
    margin: 0,
  },
  menuRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "8px",
  },
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  group: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    padding: "2px",
    minWidth: 0,
  },
  row: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    height: ROW_H,
    padding: `0 ${tokens.spacingHorizontalXS} 0 2px`,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: "transparent",
    minWidth: 0,
    "::after": {
      content: '""',
      position: "absolute",
      left: "30px",
      right: tokens.spacingHorizontalS,
      bottom: 0,
      height: "1px",
      backgroundColor: tokens.colorNeutralStroke2,
    },
    ":hover .pl-reveal": { opacity: 1 },
    ":focus-within .pl-reveal": { opacity: 1 },
  },
  rowDragging: {
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    "::after": { display: "none" },
  },
  grip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "20px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusSmall,
    background: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "grab",
    opacity: 0,
    touchAction: "none",
    transition: "opacity 120ms ease, background-color 120ms ease",
    ":hover": { backgroundColor: tokens.colorSubtleBackgroundHover, color: tokens.colorNeutralForeground2 },
    ":active": { cursor: "grabbing", backgroundColor: tokens.colorSubtleBackgroundPressed },
    ":focus-visible": { opacity: 1, outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  gripActive: { opacity: 1, cursor: "grabbing" },
  nameInput: {
    flex: 1,
    minWidth: 0,
    height: "24px",
    padding: `0 ${tokens.spacingHorizontalXS}`,
    border: "none",
    borderRadius: tokens.borderRadiusSmall,
    background: "transparent",
    color: tokens.colorNeutralForeground1,
    fontFamily: "inherit",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    textOverflow: "ellipsis",
    outline: "none",
    transition: "background-color 120ms ease, box-shadow 120ms ease",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1 },
    ":focus": {
      backgroundColor: tokens.colorNeutralBackground1,
      boxShadow: `inset 0 0 0 1px ${tokens.colorCompoundBrandStroke}`,
    },
    "::placeholder": { color: tokens.colorNeutralForeground4 },
  },
  count: {
    flexShrink: 0,
    minWidth: "28px",
    textAlign: "right",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontVariantNumeric: "tabular-nums",
    userSelect: "none",
  },
  iconBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusSmall,
    background: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    opacity: 0,
    transition: "opacity 120ms ease, background-color 120ms ease, color 120ms ease",
    ":hover": { backgroundColor: tokens.colorSubtleBackgroundHover, color: tokens.colorPaletteRedForeground1 },
    ":active": { backgroundColor: tokens.colorSubtleBackgroundPressed },
    ":focus-visible": { opacity: 1, outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  dotBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "20px",
    height: "20px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusCircular,
    background: "transparent",
    cursor: "pointer",
    transition: "background-color 120ms ease",
    ":hover": { backgroundColor: tokens.colorSubtleBackgroundHover },
    ":active": { backgroundColor: tokens.colorSubtleBackgroundPressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "0px" },
  },
  dot: {
    display: "block",
    width: "10px",
    height: "10px",
    borderRadius: tokens.borderRadiusCircular,
  },
  addBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    width: "100%",
    height: ROW_H,
    padding: `0 ${tokens.spacingHorizontalS} 0 ${tokens.spacingHorizontalXS}`,
    border: "none",
    borderRadius: tokens.borderRadiusSmall,
    background: "transparent",
    color: tokens.colorBrandForeground1,
    fontFamily: "inherit",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    textAlign: "left",
    cursor: "pointer",
    transition: "background-color 120ms ease",
    ":hover": { backgroundColor: tokens.colorSubtleBackgroundHover },
    ":active": { backgroundColor: tokens.colorSubtleBackgroundPressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
  addIcon: {
    display: "inline-flex",
    width: "20px",
    justifyContent: "center",
  },
  addRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    height: ROW_H,
    padding: `0 ${tokens.spacingHorizontalXS} 0 2px`,
  },
  addLead: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  palette: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
  },
  paletteGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(9, 20px)",
    gap: "4px",
  },
  swatch: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusCircular,
    color: tokens.colorNeutralForegroundOnBrand,
    cursor: "pointer",
    transition: "transform 120ms ease",
    ":hover": { transform: "scale(1.12)" },
    ":active": { transform: "scale(0.96)" },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
});

const CASES: MockCase[] = [
  { id: "live", label: "현재" },
  { id: "empty", label: "없음" },
  { id: "many", label: "많음" },
];

const MANY_BUCKETS: TaskBucket[] = [
  "할 일",
  "이번 주",
  "검토 대기",
  "끝난 일",
  "백로그",
  "스프린트",
  "고객 확인이 필요한 범위 변경",
  "설계",
  "개발",
  "테스트",
  "배포 대기",
  "보류",
  "차단",
  "다음 주",
  "아이디어",
  "아카이브",
].map((name, i) => ({ id: `mock-b-${i}`, name }));

const MANY_LABELS: TaskLabel[] = [
  "결함",
  "리뷰 필요",
  "문서",
  "회의",
  "데이터",
  "긴급",
  "고객 확인이 필요한 범위 변경",
  "일정",
  "품질",
  "보안",
  "교육",
  "이관",
  "운영",
  "법무",
  "기타",
  "보류",
].map((name, i) => ({ id: `mock-l-${i}`, name, color: LABEL_COLORS[i % LABEL_COLORS.length] ?? "blue" }));

type PlannerDraft = { buckets: TaskBucket[]; labels: TaskLabel[] };

function moveItem<T extends { id: string }>(items: T[], id: string, to: number): T[] {
  const from = items.findIndex((item) => item.id === id);
  if (from < 0 || to < 0 || from === to) return items;
  const next = items.slice();
  const [row] = next.splice(from, 1);
  if (!row) return items;
  next.splice(to, 0, row);
  return next;
}

/** Bucket and label management for the Planner surfaces. */
export function PlannerSettingsBody({ heading }: { heading?: string }) {
  const styles = useStyles();
  const [scene, setScene] = useState("live");
  const [draft, setDraft] = useState<PlannerDraft | null>(null);

  const pickCase = (id: string) => {
    setScene(id);
    if (id === "empty") setDraft({ buckets: [], labels: [] });
    else if (id === "many") setDraft({ buckets: MANY_BUCKETS.map((b) => ({ ...b })), labels: MANY_LABELS.map((l) => ({ ...l })) });
    else setDraft(null);
  };

  const patchBuckets = (fn: (items: TaskBucket[]) => TaskBucket[]) => {
    setDraft((cur) => (cur ? { ...cur, buckets: fn(cur.buckets) } : cur));
  };
  const patchLabels = (fn: (items: TaskLabel[]) => TaskLabel[]) => {
    setDraft((cur) => (cur ? { ...cur, labels: fn(cur.labels) } : cur));
  };

  const menu = <MockCaseMenu cases={CASES} onPick={pickCase} />;

  return (
    <>
      {heading ? (
        <div className={styles.headingRow}>
          <PageHeaderTitle className={styles.headingTitle}>{heading}</PageHeaderTitle>
          {menu}
        </div>
      ) : (
        <div className={styles.menuRow}>{menu}</div>
      )}
      <Tabs defaultValue="buckets" className="min-h-0 flex-1">
        <TabsList variant="line" className="h-8 w-full shrink-0 justify-start bg-transparent p-0">
          <TabsTrigger value="buckets" className="h-7 flex-none px-2 text-body">
            버킷
          </TabsTrigger>
          <TabsTrigger value="labels" className="h-7 flex-none px-2 text-body">
            라벨
          </TabsTrigger>
        </TabsList>
        <TabsContent value="buckets" className="min-h-0 overflow-y-auto">
          <Buckets key={scene} draft={draft?.buckets ?? null} onDraft={patchBuckets} />
        </TabsContent>
        <TabsContent value="labels" className="min-h-0 overflow-y-auto">
          <Labels key={scene} draft={draft?.labels ?? null} onDraft={patchLabels} />
        </TabsContent>
      </Tabs>
    </>
  );
}

export function PlannerSettingsDialog() {
  const open = useProjectStore((s) => s.dialog === "planner-settings");
  const canConfigure = usePlanSurface().editWbs;
  const close = () => useProjectStore.getState().openDialog(null);
  useEffect(() => {
    if (open && !canConfigure) useProjectStore.getState().openDialog(null);
  }, [open, canConfigure]);

  return (
    <AppDialog
      open={open}
      onClose={close}
      title="버킷·라벨"
      width="md"
      height={450}
      footer={
        <FuiButton appearance="primary" density="dialog" onClick={close}>
          닫기
        </FuiButton>
      }
    >
      {open ? <PlannerSettingsBody /> : null}
    </AppDialog>
  );
}

/* ---------------------------------------------------------------- buckets */

function Buckets({ draft, onDraft }: { draft: TaskBucket[] | null; onDraft: (fn: (items: TaskBucket[]) => TaskBucket[]) => void }) {
  const styles = useStyles();
  const storeBuckets = useProjectStore((s) => s.project.planner.buckets);
  const tasks = useProjectStore((s) => s.project.executionTasks);
  const buckets = draft ?? storeBuckets;
  const mocked = draft != null;
  const s = useProjectStore.getState;
  const [pendingId, setPendingId] = useState<string | null>(null);
  const countOf = (id: string) => tasks.filter((t) => t.bucketId === id).length;

  return (
    <div className={styles.panel}>
      <div className={styles.group}>
        {buckets.length === 0 && <GhostEmptyState compact title="아직 버킷이 없어요" description="아래에서 첫 열을 추가해 보세요." />}
        <SortableList items={buckets} names={buckets} onMove={(id, to) => (mocked ? onDraft((items) => moveItem(items, id, to)) : s().reorderBucket(id, to))}>
          {buckets.map((b) => (
            <SortableRow key={b.id} id={b.id} name={b.name}>
              <InlineName value={b.name} label="버킷 이름" onCommit={(v) => (mocked ? onDraft((items) => items.map((item) => (item.id === b.id ? { ...item, name: v } : item))) : s().renameBucket(b.id, v))} />
              <span className={styles.count} title={`Task ${countOf(b.id)}개`}>
                {countOf(b.id)}개
              </span>
              <RemoveButton label={`${b.name} 삭제`} onClick={() => setPendingId(b.id)} />
            </SortableRow>
          ))}
        </SortableList>
        <AddRow
          label="버킷 추가"
          placeholder="새 버킷 이름"
          onAdd={(v) => (mocked ? onDraft((items) => [...items, { id: `mock-b-${Date.now()}`, name: v }]) : s().addBucket(v))}
        />
      </div>
      <ConfirmDialog
        open={pendingId !== null}
        title="버킷 삭제"
        message="이 버킷을 삭제할까요? 안의 Task는 ‘버킷 없음’으로 남습니다."
        onCancel={() => setPendingId(null)}
        onConfirm={() => {
          if (pendingId) {
            if (mocked) onDraft((items) => items.filter((item) => item.id !== pendingId));
            else s().removeBucket(pendingId);
          }
          setPendingId(null);
        }}
      />
    </div>
  );
}

/* ---------------------------------------------------------------- labels */

function Labels({ draft, onDraft }: { draft: TaskLabel[] | null; onDraft: (fn: (items: TaskLabel[]) => TaskLabel[]) => void }) {
  const styles = useStyles();
  const storeLabels = useProjectStore((s) => s.project.planner.labels);
  const labels = draft ?? storeLabels;
  const mocked = draft != null;
  const tasks = useProjectStore((s) => s.project.executionTasks);
  const s = useProjectStore.getState;
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [newColor, setNewColor] = useState<LabelColor | null>(null);
  const countOf = (id: string) => tasks.filter((t) => t.labelIds.includes(id)).length;
  const nextColor = newColor ?? LABEL_COLORS.find((c) => !labels.some((l) => l.color === c)) ?? "blue";

  return (
    <div className={styles.panel}>
      <div className={styles.group}>
        {labels.length === 0 && <GhostEmptyState compact title="아직 라벨이 없어요" description="아래에서 첫 라벨을 만들어 보세요." />}
        <SortableList items={labels} names={labels} onMove={(id, to) => (mocked ? onDraft((items) => moveItem(items, id, to)) : s().reorderLabel(id, to))}>
          {labels.map((l) => (
            <SortableRow key={l.id} id={l.id} name={l.name}>
              <ColorPicker value={l.color} onChange={(c) => (mocked ? onDraft((items) => items.map((item) => (item.id === l.id ? { ...item, color: c } : item))) : s().updateLabel(l.id, { color: c }))} />
              <InlineName value={l.name} label="라벨 이름" onCommit={(v) => (mocked ? onDraft((items) => items.map((item) => (item.id === l.id ? { ...item, name: v } : item))) : s().updateLabel(l.id, { name: v }))} />
              <span className={styles.count} title={`Task ${countOf(l.id)}개에 사용`}>
                {countOf(l.id)}개
              </span>
              <RemoveButton label={`${l.name} 삭제`} onClick={() => setPendingId(l.id)} />
            </SortableRow>
          ))}
        </SortableList>
        <AddRow
          label="라벨 추가"
          placeholder="새 라벨 이름"
          lead={<ColorPicker value={nextColor} onChange={setNewColor} />}
          onAdd={(v) => {
            if (mocked) onDraft((items) => [...items, { id: `mock-l-${Date.now()}`, name: v, color: nextColor }]);
            else s().addLabel(v, nextColor);
            setNewColor(null);
          }}
        />
      </div>
      <ConfirmDialog
        open={pendingId !== null}
        title="라벨 삭제"
        message="이 라벨을 삭제할까요? Task에서 이 라벨이 빠집니다."
        onCancel={() => setPendingId(null)}
        onConfirm={() => {
          if (pendingId) {
            if (mocked) onDraft((items) => items.filter((item) => item.id !== pendingId));
            else s().removeLabel(pendingId);
          }
          setPendingId(null);
        }}
      />
    </div>
  );
}

/* ---------------------------------------------------------------- building blocks */

const ANNOUNCE = (names: Array<{ id: string; name: string }>): Announcements => {
  const n = (id: string | number) => names.find((x) => x.id === id)?.name ?? "항목";
  const pos = (id: string | number) => names.findIndex((x) => x.id === id) + 1;
  return {
    onDragStart: ({ active }) => `「${n(active.id)}」을(를) 들었습니다. 위아래 화살표로 옮기고 스페이스로 놓으세요.`,
    onDragOver: ({ active, over }) => (over ? `「${n(active.id)}」 → ${pos(over.id)}번째 자리` : undefined),
    onDragEnd: ({ active, over }) => (over ? `「${n(active.id)}」을(를) ${pos(over.id)}번째 자리에 놓았습니다.` : `「${n(active.id)}」 이동을 취소했습니다.`),
    onDragCancel: ({ active }) => `「${n(active.id)}」 이동을 취소했습니다.`,
  };
};

function SortableList({
  items,
  names,
  onMove,
  children,
}: {
  items: Array<{ id: string }>;
  names: Array<{ id: string; name: string }>;
  onMove: (id: string, toIndex: number) => void;
  children: ReactNode;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const to = items.findIndex((x) => x.id === over.id);
    if (to >= 0) onMove(String(active.id), to);
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={onDragEnd}
      accessibility={{
        announcements: ANNOUNCE(names),
        screenReaderInstructions: { draggable: "스페이스로 들고, 위아래 화살표로 옮긴 뒤 스페이스로 놓습니다. Esc로 취소합니다." },
      }}
    >
      <SortableContext items={items.map((x) => x.id)} strategy={verticalListSortingStrategy}>
        <div>{children}</div>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({ id, name, children }: { id: string; name: string; children: ReactNode }) {
  const styles = useStyles();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      className={mergeClasses(styles.row, isDragging && styles.rowDragging)}
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        className={mergeClasses("pl-reveal", styles.grip, isDragging && styles.gripActive)}
        aria-label={`${name} 순서 변경`}
        data-dialog-local-escape={isDragging || undefined}
        {...attributes}
        {...listeners}
      >
        <ReOrderDotsVertical16Regular />
      </button>
      {children}
    </div>
  );
}

/** Borderless text that becomes an input on hover/focus. Enter commits, Esc reverts, empty reverts. */
function InlineName({
  value,
  label,
  placeholder,
  onCommit,
}: {
  value: string;
  label: string;
  placeholder?: string;
  onCommit: (v: string) => void;
}) {
  const styles = useStyles();
  const [draft, setDraft] = useState(value);
  const [synced, setSynced] = useState(value);
  if (value !== synced) {
    setSynced(value);
    setDraft(value);
  }
  const commit = () => {
    const v = draft.trim();
    if (!v) setDraft(value);
    else if (v !== value) onCommit(v);
  };
  return (
    <input
      data-dialog-local-escape
      className={styles.nameInput}
      value={draft}
      aria-label={label}
      placeholder={placeholder}
      spellCheck={false}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          e.stopPropagation();
          setDraft(value);
          requestAnimationFrame(() => (e.target as HTMLInputElement).blur());
        }
      }}
    />
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  const styles = useStyles();
  return (
    <Tooltip content="삭제" relationship="description" withArrow>
      <button type="button" className={mergeClasses("pl-reveal", styles.iconBtn)} aria-label={label} onClick={onClick}>
        <Delete16Regular />
      </button>
    </Tooltip>
  );
}

/** Group footer: "+ 추가" text button that turns into an inline input. Enter keeps it open for rapid entry. */
function AddRow({
  label,
  placeholder,
  lead,
  onAdd,
}: {
  label: string;
  placeholder: string;
  lead?: ReactNode;
  onAdd: (v: string) => void;
}) {
  const styles = useStyles();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const rowRef = useRef<HTMLDivElement>(null);

  const submit = () => {
    const v = draft.trim();
    if (v) onAdd(v);
    setDraft("");
  };

  if (!editing) {
    return (
      <button type="button" className={styles.addBtn} onClick={() => setEditing(true)}>
        <span className={styles.addIcon}>
          <Add16Regular />
        </span>
        {label}
      </button>
    );
  }

  return (
    <div ref={rowRef} className={styles.addRow}>
      <span className={styles.addLead}>{lead ?? <Add16Regular />}</span>
      <input
        data-dialog-local-escape
        autoFocus
        className={styles.nameInput}
        value={draft}
        placeholder={placeholder}
        aria-label={placeholder}
        spellCheck={false}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.nativeEvent.isComposing) submit();
          if (e.key === "Escape") {
            e.stopPropagation();
            setDraft("");
            setEditing(false);
          }
        }}
        onBlur={(e) => {
          const next = e.relatedTarget as Element | null;
          if (next && (rowRef.current?.contains(next) || next.closest(".fui-PopoverSurface"))) return;
          submit();
          setEditing(false);
        }}
      />
    </div>
  );
}

function ColorPicker({ value, onChange }: { value: LabelColor; onChange: (c: LabelColor) => void }) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={(_, d) => setOpen(d.open)} positioning="below-start" withArrow trapFocus>
      <PopoverTrigger disableButtonEnhancement>
        <button type="button" className={styles.dotBtn} aria-label={`라벨 색: ${LABEL_STYLE[value].name}`}>
          <span className={styles.dot} style={{ backgroundColor: LABEL_DOT_COLOR[value] }} />
        </button>
      </PopoverTrigger>
      <PopoverSurface className={styles.palette}>
        <div className={styles.paletteGrid} role="radiogroup" aria-label="라벨 색">
          {LABEL_COLORS.map((c) => (
            <Tooltip key={c} content={LABEL_STYLE[c].name} relationship="label">
              <button
                type="button"
                role="radio"
                aria-checked={value === c}
                className={styles.swatch}
                style={{ backgroundColor: LABEL_DOT_COLOR[c] }}
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                }}
              >
                {value === c && <Checkmark12Filled />}
              </button>
            </Tooltip>
          ))}
        </div>
      </PopoverSurface>
    </Popover>
  );
}
